import React, { useState } from 'react';

const ProgressTracker = ({ sessions }) => {
  const [viewMode, setViewMode] = useState('daily');

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getDailyProgress = () => {
    const dailyData = {};
    const last7Days = [];

    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateString = date.toLocaleDateString();
      last7Days.push(dateString);
      dailyData[dateString] = 0;
    }

    sessions.forEach(session => {
      if (dailyData.hasOwnProperty(session.date)) {
        dailyData[session.date] += session.duration;
      }
    });

    return last7Days.map(date => ({
      date,
      duration: dailyData[date],
      sessions: sessions.filter(s => s.date === date).length
    }));
  };

  const getWeeklyProgress = () => {
    const weeklyData = {};
    const last4Weeks = [];

    for (let i = 3; i >= 0; i--) {
      const startOfWeek = new Date();
      startOfWeek.setDate(startOfWeek.getDate() - (startOfWeek.getDay() + 7 * i));
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);

      const weekLabel = `${startOfWeek.toLocaleDateString()} - ${endOfWeek.toLocaleDateString()}`;
      last4Weeks.push({
        label: weekLabel,
        start: startOfWeek,
        end: endOfWeek
      });
      weeklyData[weekLabel] = 0;
    }

    sessions.forEach(session => {
      const sessionDate = new Date(session.completedAt);
      last4Weeks.forEach(week => {
        if (sessionDate >= week.start && sessionDate <= week.end) {
          weeklyData[week.label] += session.duration;
        }
      });
    });

    return last4Weeks.map(week => ({
      week: week.label,
      duration: weeklyData[week.label],
      sessions: sessions.filter(s => {
        const sessionDate = new Date(s.completedAt);
        return sessionDate >= week.start && sessionDate <= week.end;
      }).length
    }));
  };

  const getSubjectBreakdown = () => {
    const subjectData = {};

    sessions.forEach(session => {
      if (!subjectData[session.subject]) {
        subjectData[session.subject] = {
          totalTime: 0,
          sessionCount: 0
        };
      }
      subjectData[session.subject].totalTime += session.duration;
      subjectData[session.subject].sessionCount += 1;
    });

    return Object.entries(subjectData)
      .map(([subject, data]) => ({
        subject,
        totalTime: data.totalTime,
        sessionCount: data.sessionCount,
        percentage: sessions.length > 0 ? (data.sessionCount / sessions.length) * 100 : 0
      }))
      .sort((a, b) => b.totalTime - a.totalTime);
  };

  const getGoalProgress = () => {
    const today = new Date().toLocaleDateString();
    const todaySessions = sessions.filter(s => s.date === today);
    const todayTime = todaySessions.reduce((total, session) => total + session.duration, 0);

    const thisWeek = new Date();
    const startOfWeek = new Date(thisWeek.setDate(thisWeek.getDate() - thisWeek.getDay()));
    const weekSessions = sessions.filter(s => new Date(s.completedAt) >= startOfWeek);
    const weekTime = weekSessions.reduce((total, session) => total + session.duration, 0);

    return {
      daily: {
        current: todayTime,
        goal: 2 * 60 * 60, // 2 hours
        sessions: todaySessions.length
      },
      weekly: {
        current: weekTime,
        goal: 10 * 60 * 60, // 10 hours
        sessions: weekSessions.length
      }
    };
  };

  const dailyProgress = getDailyProgress();
  const weeklyProgress = getWeeklyProgress();
  const subjectBreakdown = getSubjectBreakdown();
  const goalProgress = getGoalProgress();

  const maxDailyTime = Math.max(...dailyProgress.map(d => d.duration), 1);
  const maxWeeklyTime = Math.max(...weeklyProgress.map(w => w.duration), 1);

  return (
    <div className="progress-tracker">
      <div className="progress-header">
        <h2>Progress Tracking</h2>
        <div className="view-controls">
          <button
            className={viewMode === 'daily' ? 'active' : ''}
            onClick={() => setViewMode('daily')}
          >
            Daily
          </button>
          <button
            className={viewMode === 'weekly' ? 'active' : ''}
            onClick={() => setViewMode('weekly')}
          >
            Weekly
          </button>
          <button
            className={viewMode === 'subjects' ? 'active' : ''}
            onClick={() => setViewMode('subjects')}
          >
            Subjects
          </button>
        </div>
      </div>

      <div className="goals-section">
        <h3>Goals</h3>
        <div className="goals-grid">
          <div className="goal-item">
            <div className="goal-label">Daily Goal</div>
            <div className="goal-progress">
              <div
                className="goal-bar"
                style={{
                  width: `${Math.min((goalProgress.daily.current / goalProgress.daily.goal) * 100, 100)}%`
                }}
              ></div>
            </div>
            <div className="goal-text">
              {formatDuration(goalProgress.daily.current)} / {formatDuration(goalProgress.daily.goal)}
            </div>
          </div>

          <div className="goal-item">
            <div className="goal-label">Weekly Goal</div>
            <div className="goal-progress">
              <div
                className="goal-bar"
                style={{
                  width: `${Math.min((goalProgress.weekly.current / goalProgress.weekly.goal) * 100, 100)}%`
                }}
              ></div>
            </div>
            <div className="goal-text">
              {formatDuration(goalProgress.weekly.current)} / {formatDuration(goalProgress.weekly.goal)}
            </div>
          </div>
        </div>
      </div>

      {viewMode === 'daily' && (
        <div className="chart-section">
          <h3>Daily Progress (Last 7 Days)</h3>
          <div className="chart">
            {dailyProgress.map((day, index) => (
              <div key={index} className="chart-bar">
                <div
                  className="bar"
                  style={{
                    height: `${Math.max((day.duration / maxDailyTime) * 200, 5)}px`
                  }}
                  title={`${formatDuration(day.duration)} - ${day.sessions} sessions`}
                ></div>
                <div className="bar-label">
                  {new Date(day.date).toLocaleDateString([], {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  })}
                </div>
                <div className="bar-value">{formatDuration(day.duration)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {viewMode === 'weekly' && (
        <div className="chart-section">
          <h3>Weekly Progress (Last 4 Weeks)</h3>
          <div className="chart weekly-chart">
            {weeklyProgress.map((week, index) => (
              <div key={index} className="chart-bar">
                <div
                  className="bar"
                  style={{
                    height: `${Math.max((week.duration / maxWeeklyTime) * 200, 5)}px`
                  }}
                  title={`${formatDuration(week.duration)} - ${week.sessions} sessions`}
                ></div>
                <div className="bar-label">Week {index + 1}</div>
                <div className="bar-value">{formatDuration(week.duration)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {viewMode === 'subjects' && (
        <div className="subjects-section">
          <h3>Study Time by Subject</h3>
          {subjectBreakdown.length === 0 ? (
            <p>No subjects tracked yet. Start studying to see your progress!</p>
          ) : (
            <div className="subjects-breakdown">
              {subjectBreakdown.map((item, index) => (
                <div key={index} className="subject-progress">
                  <div className="subject-header">
                    <span className="subject-name">{item.subject}</span>
                    <span className="subject-time">{formatDuration(item.totalTime)}</span>
                  </div>
                  <div className="subject-bar">
                    <div
                      className="subject-fill"
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                  <div className="subject-stats">
                    {item.sessionCount} sessions • {item.percentage.toFixed(1)}% of total
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="stats-summary">
        <h3>Overall Statistics</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{sessions.length}</div>
            <div className="stat-label">Total Sessions</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {formatDuration(sessions.reduce((total, session) => total + session.duration, 0))}
            </div>
            <div className="stat-label">Total Study Time</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {sessions.length > 0 ?
                formatDuration(sessions.reduce((total, session) => total + session.duration, 0) / sessions.length) :
                '0m'
              }
            </div>
            <div className="stat-label">Average Session</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{subjectBreakdown.length}</div>
            <div className="stat-label">Subjects Studied</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressTracker;